import { jsPDF } from "jspdf";

const C = {
  cyan: [0, 200, 220],
  violet: [176, 38, 255],
  dark: [8, 11, 22],
  gray: [110, 116, 140],
  ink: [22, 24, 34],
};

// Datos de la empresa
const COMPANY = {
  name: "CYVRLABS",
  web: "cyvrlabs.com",
  domain: "cyvrlabs.com",
  street: "Calle Castelló, 3",
  cp: "46815",
  town: "La Llosa de Ranes (Xàtiva)",
  province: "Valencia",
  phone: "+34 607 20 21 06",
  email: "contacto@cyvrlabs.com",
};

// 5 niveles de seguridad (peor -> mejor)
const LEVELS = [
  { key: "F", label: "Crítico", range: "0–39", color: [220, 40, 60] },
  { key: "D", label: "Bajo", range: "40–54", color: [230, 120, 40] },
  { key: "C", label: "Medio", range: "55–69", color: [230, 190, 40] },
  { key: "B", label: "Bueno", range: "70–84", color: [120, 200, 90] },
  { key: "A", label: "Excelente", range: "85–100", color: [40, 190, 110] },
];

const SEV = {
  crítica: { label: "CRÍTICA", color: [200, 30, 60] },
  alta: { label: "ALTA", color: [230, 90, 40] },
  media: { label: "MEDIA", color: [230, 180, 40] },
  baja: { label: "BAJA", color: [90, 160, 220] },
  info: { label: "INFO", color: [130, 140, 160] },
};

const loadImageDataURL = (url, maxW = 420) =>
  new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      try {
        const scale = Math.min(1, maxW / img.naturalWidth);
        const cw = Math.round(img.naturalWidth * scale);
        const ch = Math.round(img.naturalHeight * scale);
        const canvas = document.createElement("canvas");
        canvas.width = cw;
        canvas.height = ch;
        canvas.getContext("2d").drawImage(img, 0, 0, cw, ch);
        resolve({ data: canvas.toDataURL("image/png"), w: cw, h: ch });
      } catch {
        resolve(null);
      }
    };
    img.onerror = () => resolve(null);
    img.src = url;
  });

export const buildScanDoc = async (scan) => {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();
  const M = 40;
  let y = 0;

  const ensure = (space) => {
    if (y + space > H - 50) {
      doc.addPage();
      y = 50;
    }
  };

  // ---------- Header (dark band with logo) ----------
  doc.setFillColor(...C.dark);
  doc.rect(0, 0, W, 116, "F");
  doc.setFillColor(...C.cyan);
  doc.rect(0, 114, W, 3, "F");

  const logo = await loadImageDataURL("/cyvr-logo-full.png");
  if (logo) {
    const lw = 170;
    const lh = (logo.h / logo.w) * lw;
    doc.addImage(logo.data, "PNG", M, (116 - lh) / 2, lw, lh);
  } else {
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(22);
    doc.text("CYVR LABS", M, 52);
  }
  doc.setTextColor(0, 200, 220);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text("Informe de escaneo de seguridad", W - M, 46, { align: "right" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(180, 184, 200);
  doc.text("Ciberseguridad y defensa ante los hackers de hoy", W - M, 64, { align: "right" });
  doc.text(`Realizado: ${new Date(scan.created_at).toLocaleString("es-ES")}`, W - M, 82, { align: "right" });

  // ---------- Info blocks ----------
  y = 150;
  const colW = (W - 2 * M - 20) / 2;
  const boxH = 150;

  const infoBox = (x, title, rows) => {
    doc.setDrawColor(225, 227, 235);
    doc.setFillColor(248, 249, 252);
    doc.roundedRect(x, y, colW, boxH, 6, 6, "FD");
    doc.setFillColor(...C.ink);
    doc.roundedRect(x, y, colW, 22, 6, 6, "F");
    doc.rect(x, y + 11, colW, 11, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.text(title, x + 12, y + 15);
    let ry = y + 40;
    doc.setFontSize(9);
    rows.forEach(([k, v]) => {
      doc.setFont("helvetica", "bold");
      doc.setTextColor(...C.gray);
      doc.text(`${k}`, x + 12, ry);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(...C.ink);
      doc.text(`${v}`, x + 92, ry, { maxWidth: colW - 100 });
      ry += 15.5;
    });
  };

  infoBox(M, "DATOS DE LA EMPRESA", [
    ["Nombre", COMPANY.name],
    ["Web", COMPANY.web],
    ["Dominio", COMPANY.domain],
    ["Dirección", COMPANY.street],
    ["CP / Población", `${COMPANY.cp} · ${COMPANY.town}`],
    ["Provincia", COMPANY.province],
    ["NIF", COMPANY.nif],
  ]);
  infoBox(M + colW + 20, "DATOS DEL ANÁLISIS", [
    ["Objetivo", scan.target],
    ["Dirección IP", scan.ip || "—"],
    ["Fecha y hora", new Date(scan.created_at).toLocaleString("es-ES")],
    ["Teléfono", COMPANY.phone],
    ["Email", COMPANY.email],
    ["Web", COMPANY.web],
  ]);

  y += boxH + 26;

  // ---------- Section helper ----------
  const section = (title) => {
    ensure(60);
    doc.setFillColor(...C.ink);
    doc.rect(M, y, W - 2 * M, 22, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.text(title, M + 10, y + 15);
    y += 34;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
  };
  const line = (text, color = [40, 40, 50]) => {
    ensure(18);
    doc.setTextColor(...color);
    doc.setFontSize(10);
    doc.text(text, M + 8, y);
    y += 16;
  };

  // ---------- Nivel de seguridad (5 niveles) ----------
  section("Nivel de seguridad del escaneo");
  const currentIdx = LEVELS.findIndex((l) => l.key === scan.grade);
  const barW = W - 2 * M;
  const segW = barW / 5;
  const barY = y;
  LEVELS.forEach((lv, i) => {
    const x = M + i * segW;
    doc.setFillColor(...lv.color);
    doc.rect(x, barY, segW, 26, "F");
    if (i === currentIdx) {
      doc.setDrawColor(20, 20, 30);
      doc.setLineWidth(2);
      doc.rect(x + 1, barY + 1, segW - 2, 24, "S");
      doc.setLineWidth(1);
    }
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.text(lv.label, x + segW / 2, barY + 12, { align: "center" });
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);
    doc.text(lv.range, x + segW / 2, barY + 21, { align: "center" });
  });
  // marker triangle over current band
  if (currentIdx >= 0) {
    const mx = M + currentIdx * segW + segW / 2;
    doc.setFillColor(20, 20, 30);
    doc.triangle(mx - 5, barY - 3, mx + 5, barY - 3, mx, barY + 3, "F");
  }
  y = barY + 42;
  const lv = LEVELS[currentIdx] || LEVELS[0];
  doc.setFont("helvetica", "bold");
  doc.setFontSize(20);
  doc.setTextColor(...lv.color);
  doc.text(`${scan.score}/100`, M + 8, y);
  doc.setFontSize(12);
  doc.text(`Nota ${scan.grade} · ${lv.label}`, M + 120, y);
  y += 26;

  // ---------- Puertos ----------
  const ports = scan.ports || [];
  section(`Puertos abiertos detectados (${ports.length})`);
  if (ports.length === 0) line("No se ha detectado ningún puerto común abierto.", [40, 120, 60]);
  ports.forEach((p) =>
    line(
      `• Puerto ${p.port}  —  ${p.service}  —  ${p.risk === "high" ? "RIESGO ALTO" : "Normal"}`,
      p.risk === "high" ? [200, 40, 80] : [40, 120, 60]
    )
  );

  // ---------- Cabeceras ----------
  const headers = scan.headers || { present: [], missing: [] };
  section("Cabeceras de seguridad HTTP");
  (headers.present || []).forEach((h) => line(`✓ Presente: ${h}`, [40, 120, 60]));
  (headers.missing || []).forEach((h) => line(`✗ Ausente: ${h}`, [200, 120, 40]));
  if (headers.server) line(`Servidor: ${headers.server}`, C.gray);

  // ---------- TLS ----------
  const tls = scan.tls || {};
  section("Certificado TLS");
  if (tls.valid) {
    line(`Protocolo: ${tls.protocol}`);
    line(`Emisor: ${tls.issuer || "—"}`);
    line(`Common Name: ${tls.common_name || "—"}`);
    line(`Expira: ${tls.expires}  (${tls.days_left} días restantes)`);
  } else {
    line("Sin certificado HTTPS válido en el puerto 443.", [200, 120, 40]);
  }

  // ---------- Vulnerabilidades ----------
  const vulns = scan.vulnerabilities || [];
  section(`Escaneo de vulnerabilidades (${vulns.length})`);
  if (vulns.length === 0) {
    line("No se han detectado vulnerabilidades destacables.", [40, 120, 60]);
  }
  vulns.forEach((v, i) => {
    const sev = SEV[v.severity] || SEV.info;
    ensure(70);
    // severity chip
    doc.setFillColor(...sev.color);
    doc.roundedRect(M + 8, y - 10, 54, 15, 3, 3, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.text(sev.label, M + 35, y, { align: "center" });
    // title
    doc.setTextColor(...C.ink);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10.5);
    const titleLines = doc.splitTextToSize(`${i + 1}. ${v.title}`, W - 2 * M - 80);
    doc.text(titleLines, M + 72, y);
    y += titleLines.length * 13 + 2;
    // description
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(60, 62, 74);
    const dLines = doc.splitTextToSize(v.description, W - 2 * M - 24);
    ensure(dLines.length * 12 + 24);
    doc.text(dLines, M + 12, y);
    y += dLines.length * 12 + 2;
    // recommendation
    doc.setTextColor(...C.cyan);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8.5);
    doc.text("Recomendación:", M + 12, y);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(70, 90, 110);
    const rLines = doc.splitTextToSize(v.recommendation, W - 2 * M - 100);
    doc.text(rLines, M + 90, y);
    y += Math.max(rLines.length * 11, 11) + 12;
    doc.setDrawColor(235, 236, 240);
    doc.line(M + 8, y - 6, W - M, y - 6);
  });

  // ---------- Footer on every page ----------
  const pages = doc.getNumberOfPages();
  for (let i = 1; i <= pages; i++) {
    doc.setPage(i);
    doc.setDrawColor(230, 230, 235);
    doc.line(M, H - 40, W - M, H - 40);
    doc.setFontSize(8);
    doc.setTextColor(...C.gray);
    doc.text(
      `${COMPANY.name} · ${COMPANY.web} · ${COMPANY.phone} · ${COMPANY.email}`,
      M,
      H - 26
    );
    doc.text(`Página ${i}/${pages} · Uso autorizado únicamente`, W - M, H - 26, { align: "right" });
  }

  return doc;
};

export const exportScanPdf = async (scan) => {
  const doc = await buildScanDoc(scan);
  doc.save(`cyvr-scan-${scan.target}-${scan.grade}.pdf`);
};

export const scanPdfBase64 = async (scan) => {
  const doc = await buildScanDoc(scan);
  return doc.output("datauristring").split(",")[1];
};
